<script setup>
// NOTICE: KAPTION-INTEGRATION
// ctwa-ad-header-badge.BADGE.1..4, POPOVER.1..6
// "This chat came from an ad": blue megaphone pill in the conversation
// header (reuses the campaign indicator pattern) with a popover that shows the
// ad creative Meta sent. It is the originating ad — never a campaign name.
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { Dropdown as VDropdown } from 'floating-vue';
import {
  ctwaPlacement,
  ctwaPreviewImage,
} from 'dashboard/helper/kaptionCtwaReferral';

const props = defineProps({
  attribution: {
    type: Object,
    required: true,
  },
});

const { t, locale } = useI18n();

const ad = computed(() => props.attribution.last);
const firstAd = computed(() =>
  props.attribution.hasDifferentFirstTouch ? props.attribution.first : null
);

// ctwa-ad-header-badge.BADGE.2 - the creative headline is shown as-is;
// without one the pill still says it is an ad.
const pillText = computed(() => ad.value.headline || t('KAPTION_CTWA.AD'));

const placement = computed(() => ctwaPlacement(ad.value.sourceUrl));
const placementLabels = {
  FACEBOOK: () => t('KAPTION_CTWA.PLACEMENT.FACEBOOK'),
  INSTAGRAM: () => t('KAPTION_CTWA.PLACEMENT.INSTAGRAM'),
  WHATSAPP_STATUS: () => t('KAPTION_CTWA.PLACEMENT.WHATSAPP_STATUS'),
};
const placementLabel = computed(
  () => placementLabels[placement.value]?.() || null
);

// ctwa-ad-header-badge.POPOVER.3 - signed Meta media links expire (~4 days);
// expired or failing images fall back to a text notice instead of a broken
// image.
const imageFailed = ref(false);
watch(
  () => ad.value.imageUrl,
  () => {
    imageFailed.value = false;
  }
);
const previewImage = computed(() =>
  imageFailed.value ? null : ctwaPreviewImage(ad.value)
);
const hadMedia = computed(() =>
  Boolean(ad.value.imageUrl || ad.value.thumbnailUrl || ad.value.videoUrl)
);

// ctwa-ad-header-badge.POPOVER.5 - Meta omits ctwa_clid for WhatsApp
// Status ads; only claim that reason when the ad actually ran there.
const clickIdLabel = computed(() => {
  if (ad.value.hasClickId) return t('KAPTION_CTWA.CLICK_ID_PRESENT');
  return placement.value === 'WHATSAPP_STATUS'
    ? t('KAPTION_CTWA.CLICK_ID_MISSING_STATUS')
    : t('KAPTION_CTWA.CLICK_ID_MISSING');
});

const capturedAt = computed(() => {
  if (!ad.value.capturedAt) return null;
  const date = new Date(ad.value.capturedAt);
  if (Number.isNaN(date.getTime())) return null;
  return new Intl.DateTimeFormat(locale.value, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(date);
});

const summarize = text =>
  text && text.length > 90 ? `${text.slice(0, 90).trimEnd()}…` : text;
</script>

<template>
  <VDropdown
    :triggers="['hover', 'focus', 'touch']"
    :popper-triggers="['hover', 'focus']"
    no-auto-focus
    popper-class="kaption-ctwa-popper"
    :delay="{ show: 150, hide: 150 }"
    placement="bottom-start"
    :distance="6"
    class="min-w-0"
  >
    <!-- ctwa-ad-header-badge.BADGE.1 / .BADGE.3 - own tab stop, and clicks
         don't reach the header's contact-sidebar toggle. -->
    <button
      type="button"
      data-test-id="ctwa-ad-badge"
      class="inline-flex items-center min-w-0 max-w-[16rem] px-2 py-0.5 rounded-full text-xs font-medium bg-n-blue-2 text-n-blue-11 border border-n-blue-5 hover:bg-n-blue-3 focus-visible:outline focus-visible:outline-2 focus-visible:outline-n-blue-8"
      :aria-label="t('KAPTION_CTWA.BADGE_ARIA', { headline: pillText })"
      @click.stop
    >
      <fluent-icon icon="megaphone" size="12" class="mr-1 flex-shrink-0" />
      <span class="font-semibold mr-1 flex-shrink-0">
        {{ t('KAPTION_CTWA.AD') }}
      </span>
      <span v-if="ad.headline" class="truncate">
        {{ t('KAPTION_CTWA.PILL_HEADLINE', { headline: ad.headline }) }}
      </span>
    </button>

    <template #popper>
      <div
        data-test-id="ctwa-ad-popover"
        class="w-80 max-w-[calc(100vw-2rem)] p-3 flex flex-col gap-2 text-sm text-n-slate-12 bg-n-solid-2 border border-n-weak rounded-xl shadow-lg [&_p]:m-0 [&_dd]:m-0"
        @click.stop
      >
        <div class="flex items-center gap-2">
          <span
            class="inline-flex items-center justify-center w-6 h-6 rounded-md bg-n-blue-3 text-n-blue-11 flex-shrink-0"
          >
            <fluent-icon icon="megaphone" size="14" />
          </span>
          <div class="min-w-0">
            <p class="font-semibold leading-tight">
              {{ t('KAPTION_CTWA.TITLE') }}
            </p>
            <p v-if="placementLabel" class="text-xs text-n-slate-11">
              {{ placementLabel }}
            </p>
          </div>
        </div>

        <!-- ctwa-ad-header-badge.POPOVER.3 -->
        <img
          v-if="previewImage"
          :src="previewImage"
          :alt="t('KAPTION_CTWA.IMAGE_ALT')"
          referrerpolicy="no-referrer"
          loading="lazy"
          class="w-full max-h-40 object-cover rounded-lg bg-n-alpha-2"
          @error="imageFailed = true"
        />
        <p
          v-else-if="hadMedia"
          data-test-id="ctwa-ad-media-unavailable"
          class="text-xs text-n-slate-11 rounded-lg bg-n-alpha-1 px-2 py-1.5"
        >
          {{ t('KAPTION_CTWA.MEDIA_UNAVAILABLE') }}
        </p>

        <!-- ctwa-ad-header-badge.POPOVER.1 - labelled as the ad's own text -->
        <dl class="flex flex-col gap-1.5">
          <div v-if="ad.headline">
            <dt class="text-xs text-n-slate-11">
              {{ t('KAPTION_CTWA.HEADLINE') }}
            </dt>
            <dd class="font-medium">{{ ad.headline }}</dd>
          </div>
          <div v-if="ad.body">
            <dt class="text-xs text-n-slate-11">
              {{ t('KAPTION_CTWA.BODY') }}
            </dt>
            <dd class="whitespace-pre-line line-clamp-4">{{ ad.body }}</dd>
          </div>
          <div v-if="ad.welcomeMessage">
            <dt class="text-xs text-n-slate-11">
              {{ t('KAPTION_CTWA.GREETING') }}
            </dt>
            <dd class="italic">
              {{ t('KAPTION_CTWA.QUOTED', { text: ad.welcomeMessage }) }}
            </dd>
          </div>
        </dl>

        <a
          v-if="ad.sourceUrl"
          :href="ad.sourceUrl"
          target="_blank"
          rel="noopener noreferrer"
          class="inline-flex items-center gap-1 text-n-blue-11 hover:underline w-fit"
        >
          {{ t('KAPTION_CTWA.VIEW_AD') }}
          <fluent-icon icon="open" size="12" />
        </a>

        <!-- ctwa-ad-header-badge.POPOVER.4 -->
        <div
          v-if="firstAd"
          data-test-id="ctwa-ad-first-touch"
          class="rounded-lg bg-n-alpha-1 px-2 py-1.5 text-xs"
        >
          <p class="text-n-slate-11">{{ t('KAPTION_CTWA.FIRST_AD') }}</p>
          <p class="text-n-slate-12">
            {{
              firstAd.body
                ? t('KAPTION_CTWA.FIRST_AD_SUMMARY', {
                    headline: firstAd.headline || t('KAPTION_CTWA.AD'),
                    body: summarize(firstAd.body),
                  })
                : firstAd.headline || t('KAPTION_CTWA.AD')
            }}
          </p>
        </div>

        <!-- ctwa-ad-header-badge.POPOVER.2 / .POPOVER.5 -->
        <div
          class="border-t border-n-weak pt-2 flex flex-col gap-0.5 text-xs text-n-slate-11 [&_p]:m-0"
        >
          <p v-if="capturedAt">
            {{ t('KAPTION_CTWA.RECEIVED_AT', { date: capturedAt }) }}
          </p>
          <p v-if="ad.sourceId" class="break-all">
            {{ t('KAPTION_CTWA.AD_ID', { id: ad.sourceId }) }}
          </p>
          <p>
            {{ clickIdLabel }}
          </p>
          <p class="text-n-slate-10">
            {{ t('KAPTION_CTWA.NO_CAMPAIGN_NOTE') }}
          </p>
        </div>
      </div>
    </template>
  </VDropdown>
</template>

<style>
/* NOTICE: KAPTION-INTEGRATION - ctwa-ad-header-badge.POPOVER.7
   floating-vue's dropdown theme paints a fixed white panel; the popover is
   teleported to <body>, so this can't be scoped. The panel itself is drawn
   with theme tokens above, which follow light/dark mode. */
.kaption-ctwa-popper .v-popper__inner {
  background: transparent;
  border: 0;
  box-shadow: none;
  color: inherit;
}

.kaption-ctwa-popper .v-popper__arrow-container {
  display: none;
}
</style>
